import { getSite } from "@src/global_functions/postgrestApi";
import { SITE_SUMMARY_FIELDS } from "@src/global_functions/sites/sitesHelpers";
import { skipToken, useQuery } from "@tanstack/react-query";
import { atom } from "jotai";
import { createContext, useContext, useMemo } from "react";
import { useParams } from "react-router-dom";

import { useSitesSummaryQuery } from "@src/global_functions/sites/sitesQueries";
import type { SummaryOverview } from "../types";

export type SitesContextType = {
  currentAccountSites: Array<SummaryOverview>;
  refreshSite: (siteId: string | number) => Promise<void>;
  isLoading: boolean;
};

const SitesContext = createContext<SitesContextType>({
  currentAccountSites: [],
  refreshSite: async () => {},
  isLoading: false,
});

export default SitesContext;

// The site requested must be administered by the current account
export const useSite = (siteId?: string | number | null) => {
  siteId = typeof siteId === "string" ? Number(siteId) : siteId;

  const { currentAccountSites } = useContext(SitesContext);

  const siteFromCurrentAccountSites = useMemo(() => {
    if (currentAccountSites) {
      return currentAccountSites.find(({ id }) => id === siteId);
    }
  }, [currentAccountSites, siteId]);

  const enabled = typeof siteId === "number" && !siteFromCurrentAccountSites;
  const { data: site } = useQuery({
    queryKey: enabled ? ["site", siteId] : ["disabled-query"],
    queryFn: enabled
      ? async () => {
          const result = await getSite(siteId!, {
            includeInactive: true,
            fields: SITE_SUMMARY_FIELDS,
          });
          return result ?? null;
        }
      : skipToken,
    enabled,
  });

  return siteFromCurrentAccountSites ?? site;
};

// DEPRECATED: Sites should only be retrieved per account, site ID, or paginated search
export const useActiveSitesSummaryQuery = () => {
  const { data: allSites, isPending } = useSitesSummaryQuery({
    enabled: true,
    includeInactiveSites: false,
  });

  return {
    data: allSites ?? [],
    isPending,
  };
};

export const useSitesLoading = () => {
  const { isLoading: loading } = useContext(SitesContext);
  return loading;
};

export function useSiteIdParam() {
  const params = useParams<{ siteId: string }>();
  const siteId = Number(params.siteId);

  if (Number.isNaN(siteId)) {
    throw new Error(`Site ID query param is NaN: ${params.siteId}`);
  }

  return siteId;
}

// Similar to `useSiteIdParam` but does not throw an error if siteId param is not a number or undefined
export function useOptionalSiteIdParam() {
  const params = useParams<{ siteId: string }>();
  const siteId = Number(params.siteId);

  if (Number.isNaN(siteId)) {
    return undefined;
  }

  return siteId;
}

export const useCurrentSite = () => {
  const siteId = useSiteIdParam();

  return useSite(siteId);
};

// Provide an atom to get current site since useSiteIdParam only works for components nested under SiteDashboard
export const currentSiteIdAtom = atom<number | null>(null);
